import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Facebook, Menu, X, Info, Star, ShieldCheck, Layers3, Sofa } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  RKD PLUS Construction & Interiors — concept site. Design/architecture-led
  identity: navy/white, editorial, built around its real PCAB license, its own
  drone/jobsite photography and its construction+interiors+cabinetry cross-sell.
*/

const NAVY = "#0B2545";
const NAVY_LIGHT = "#13315C";
const CREAM = "#F6F7FA";
const INK = "#1A1A1A";
const GOLD = "#C9A34E";

const FB = "https://www.facebook.com/RKDCustomBuilders/";
const CABINETS_FB = "https://www.facebook.com/RKDModularCabinets/";
const NAME = "RKD PLUS Construction & Interiors";
const SLUG = "rkd-plus-construction-concept";
const P = "/construction/rkdplus";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function RkdPlusConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["Capabilities", "#capabilities"], ["Projects", "#projects"], ["Interiors", "#interiors"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Libre Franklin', sans-serif" }} data-testid="rkdplus-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Libre+Franklin:wght@300;400;500;600;700&family=Fraunces:wght@500;600&display=swap');
        .rkd-serif { font-family: 'Fraunces', serif; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-[#061A33] text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2 px-4">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.
        </span>
      </div>

      <header className={`sticky top-0 z-40 bg-white transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.08)]" : ""}`}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="rkd-logo" className="flex items-center gap-3">
            <img src={`${P}/logo.jpg`} alt={NAME} className="h-11 w-11 object-contain" />
            <span className="rkd-serif text-lg" style={{ color: NAVY }}>RKD PLUS</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-medium" style={{ color: `${INK}99` }}>{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="rkd-header-cta" className="hidden sm:inline-flex rounded-sm px-5 py-2.5 text-sm font-semibold text-white" style={{ background: NAVY }}>
            Request a Quotation
          </a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="rkd-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="rkd-mobile-cta" className="block mt-2 text-center rounded-sm px-5 py-3 text-sm font-semibold text-white" style={{ background: NAVY }}>Request a Quotation</a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-[82vh] flex items-end" data-testid="rkd-hero">
        <img src={`${P}/site-dumaguete.jpg`} alt="An RKD PLUS jobsite in Dumaguete City" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${NAVY}22 0%, ${NAVY}CC 65%, ${NAVY}F5 100%)` }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 pt-40 w-full">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Sibulan, Negros Oriental &middot; PCAB License No. 61020</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-tight text-white rkd-serif font-medium">Construction and Interiors, Under One Roof.</h1>
            <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-xl">
              Trained, experienced construction professionals delivering residential builds and interior fit-outs across Sibulan, Valencia, Tanjay and greater Negros Oriental.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="rkd-hero-cta" className="rounded-sm px-7 py-3.5 text-sm font-semibold text-white" style={{ background: NAVY }}>Request a Quotation</a>
              <a href="#projects" data-testid="rkd-hero-projects" className="rounded-sm border border-white/40 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors">View Our Projects</a>
            </div>
            <p className="mt-7 text-sm text-white/70" data-testid="rkd-trust-signal">
              <Star size={14} className="inline -mt-0.5" style={{ color: GOLD }} /> 5.0 Google Rating · 5 Reviews · Sibulan
            </p>
          </motion.div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-xl mb-14">
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: NAVY }}>Licensed &amp; Cross-Disciplinary</span>
          <h2 className="mt-3 text-4xl sm:text-5xl rkd-serif font-medium leading-snug">Capabilities</h2>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-10">
          {[
            [ShieldCheck, "Licensed Construction", "PCAB License No. 61020 — construction and interior services delivered by trained professionals."],
            [Layers3, "Residential Builds", "New residential construction across Sibulan, Valencia, Tanjay and nearby Negros Oriental areas."],
            [Sofa, "Interiors & Cabinetry", "Interior fit-outs and a related modular cabinetry line, so finishing stays in the same hands as the build."],
          ].map(([Icon, t, b]) => (
            <motion.div key={t} {...fadeUp}>
              <Icon size={26} style={{ color: NAVY }} />
              <h3 className="mt-4 text-xl font-semibold rkd-serif">{t}</h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: `${INK}80` }}>{b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="py-20 md:py-28" style={{ background: NAVY }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>On Site</span>
            <h2 className="mt-3 text-4xl sm:text-5xl text-white rkd-serif font-medium leading-snug">Our Projects</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-8">
            {[
              { img: `${P}/site-sanjose.jpg`, label: "Groundwork underway, San Jose" },
              { img: `${P}/site-dumaguete.jpg`, label: "Residential build in progress, Dumaguete City" },
            ].map((it) => (
              <motion.div key={it.img} {...fadeUp} data-testid="rkd-project-item" className="overflow-hidden rounded-sm" style={{ boxShadow: "0 12px 40px rgba(0,0,0,0.35)" }}>
                <img src={it.img} alt={it.label} className="w-full h-72 object-cover" loading="lazy" />
                <div className="bg-white/5 px-5 py-4"><p className="text-sm text-white/70">{it.label}</p></div>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="mt-10 rounded-sm overflow-hidden">
            <img src={`${P}/collage.png`} alt="A mix of RKD PLUS design concepts and jobsite photography" className="w-full object-cover" loading="lazy" />
            <p className="bg-white/5 px-5 py-3 text-xs text-white/60">A mix of design concepts and real jobsite photography from recent projects.</p>
          </motion.div>
          <motion.p {...fadeUp} className="mt-8 max-w-3xl text-sm leading-relaxed text-white/70">
            Independent supplier updates also document RKD PLUS work on a 2025 warehouse project using longspan roofing and bended panels, and a 2024 A-frame house project involving roofing, cladding and soffit installation.
          </motion.p>
        </div>
      </section>

      {/* INTERIORS */}
      <section id="interiors" className="max-w-6xl mx-auto px-5 sm:px-8 py-20 md:py-28 grid md:grid-cols-2 gap-14 items-center">
        <motion.div {...fadeUp}>
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: NAVY }}>Interiors</span>
          <h2 className="mt-3 text-4xl rkd-serif font-medium leading-snug">Finishing That Matches the Build</h2>
          <p className="mt-5 text-lg leading-relaxed" style={{ color: `${INK}90` }}>
            Our interiors work is backed by RKD PLUS Modular Cabinets — a related cabinetry business that lets us carry a project from structure through built-in finishing.
          </p>
          <a href={CABINETS_FB} target="_blank" rel="noopener noreferrer" data-testid="rkd-cabinets-link" className="mt-5 inline-block text-sm font-semibold hover:underline" style={{ color: NAVY }}>See RKD PLUS Modular Cabinets on Facebook &rarr;</a>
        </motion.div>
        <motion.div {...fadeUp} className="rounded-sm overflow-hidden" style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}>
          <img src={`${P}/interior-detail.jpg`} alt="Interior finishing detail from an RKD PLUS project" className="w-full h-96 object-cover" />
        </motion.div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: NAVY }}>Get In Touch</span>
            <h2 className="mt-3 text-4xl rkd-serif font-medium leading-snug">Request a Project Quotation</h2>
            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: NAVY }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Address</div><div>Diversion Road, Purok 6, Sibulan, Negros Oriental</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Phone size={20} style={{ color: NAVY }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Phone</div><a href="tel:+639487349798" className="hover:underline">0948 734 9798</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Mail size={20} style={{ color: NAVY }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Email</div><a href="mailto:rkdplus30@gmail.com" className="hover:underline">rkdplus30@gmail.com</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={20} style={{ color: NAVY }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="rkd-facebook-link" className="hover:underline">facebook.com/RKDCustomBuilders</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="rkd"
              accent={NAVY}
              accentText="#FFFFFF"
              heading="Request a Project Quotation"
              subheading="Construction, interiors or both — tell us about the project and we'll follow up."
              projectTypes={["Residential Construction", "Interior Fit-Out", "Construction + Interiors", "Other"]}
              phone="+639487349798"
              phoneDisplay="0948 734 9798"
              messengerUrl={FB}
              email="rkdplus30@gmail.com"
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14" style={{ background: NAVY_LIGHT, color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={`${P}/logo.jpg`} alt={NAME} className="h-10 w-10 object-contain" />
              <span className="rkd-serif text-white text-lg">RKD PLUS</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">Construction &amp; Interiors, PCAB License No. 61020 — Sibulan, Negros Oriental.</p>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Capabilities</div>
            <ul className="text-sm space-y-2">
              <li>Residential Construction</li>
              <li>Interior Fit-Out</li>
              <li>Modular Cabinetry</li>
            </ul>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Contact</div>
            <p className="text-sm">Diversion Road, Purok 6,<br />Sibulan, Negros Oriental</p>
            <p className="text-sm mt-2">0948 734 9798</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {NAME}. Project details and availability should be confirmed directly with the company.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 rounded-sm px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl" style={{ background: NAVY }}>
        Message RKD PLUS
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
